import { InputMask } from "@redotech/react-util/input-mask";
import ArrowUp from "@redotech/redo-icons/arrow-up_filled.svg";
import classNames from "classnames";
import {
  ForwardedRef,
  forwardRef,
  HTMLInputTypeAttribute,
  JSXElementConstructor,
  memo,
  ReactNode,
} from "react";
import { Flex } from "../../flex";
import { SaveIndicator } from "../../save-indicator/save-indicator";
import { RedoButton } from "../buttons/redo-button";
import { BaseRedoInputContainer } from "./base-redo-input-container";
import * as redoTextInputCss from "./base-redo-input.module.css";
import {
  BaseRedoInput,
  RedoInputSize,
  RedoInputState,
  sizeStyles,
} from "./base-redo-text-input";
import { BaseRedoInputInfoTooltip } from "./base-redo-text-input-info-tooltip";
/**
 * @param inputTypeHint -- if you have one of these types of input attributes,
 * browser autofill and other helpful features will work better if you define it.
 *
 * @param required -- when used in a form, will prevent the form from submitting if the input is empty.
 *
 * @param dangerousStyleThatShouldOnlyBeUsedForMerchantBranding -- this prop should only be defined
 * when we want the input to follow a merchant's branding. Do not use it to style this component for a Redo use case.
 */
export interface RedoTextInputProps {
  value: string;
  setValue(value: string): void;
  placeholder?: string;
  placeholderStyle?: React.CSSProperties;
  size?: RedoInputSize;
  state?: RedoInputState;
  label?: string;
  description?: string;
  errorText?: string;
  IconLeading?: JSXElementConstructor<any>;
  IconTrailing?: JSXElementConstructor<any>;
  infoTooltip?: string;
  inputTypeHint?: HTMLInputTypeAttribute;
  required?: boolean;
  maxLength?: number;
  min?: number;
  max?: number;
  htmlPattern?: string;
  constrainToInputLength?: boolean;
  className?: string;
  dangerousStyleThatShouldOnlyBeUsedForMerchantBranding?: React.CSSProperties;
  name?: string;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  onPaste?: React.ClipboardEventHandler<HTMLInputElement>;
  mask?: InputMask;
  autoCompleteDisabled?: boolean;
  trailingActions?: ReactNode;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  fullWidth?: boolean;
  autoFocus?: boolean;
  toolbar?: ReactNode;
  step?: number;
  autoSave?: { isSaving: boolean };
  submit?: () => void; //Optional submit button
  dataTestId?: string;
  onWheel?: React.WheelEventHandler<HTMLInputElement>;
  uiSelector?: string;
  /** Options to display as autocomplete suggestions via a datalist element */
  datalistOptions?: string[];
}

export const RedoTextInput = memo(
  forwardRef(function RedoTextInput(
    {
      size = "sm",
      value,
      placeholder,
      placeholderStyle,
      setValue,
      IconLeading,
      IconTrailing,
      infoTooltip,
      inputTypeHint,
      label,
      description,
      errorText,
      state = "default",
      className,
      constrainToInputLength,
      required,
      htmlPattern,
      maxLength,
      dangerousStyleThatShouldOnlyBeUsedForMerchantBranding,
      name,
      onFocus,
      onBlur,
      onPaste,
      mask,
      autoCompleteDisabled,
      trailingActions,
      onKeyDown,
      fullWidth,
      toolbar,
      autoFocus,
      min,
      max,
      step,
      autoSave,
      submit,
      dataTestId,
      onWheel,
      uiSelector,
      datalistOptions,
    }: RedoTextInputProps,
    ref: ForwardedRef<HTMLInputElement>,
  ) {
    const readonly = state === "readonly";

    const innerPy = toolbar ? "md" : undefined;

    return (
      <BaseRedoInputContainer
        className={className}
        dangerousStyleThatShouldOnlyBeUsedForMerchantBranding={
          dangerousStyleThatShouldOnlyBeUsedForMerchantBranding
        }
        description={description}
        dynamicHeight={!!toolbar}
        errorText={errorText}
        fullWidth={fullWidth}
        label={label}
        size={size}
        state={state}
        uiSelector={uiSelector}
      >
        <Flex dir="column" gap="xs" grow={1} maxW="full" py={innerPy}>
          <Flex align="center" dir="row" grow={1}>
            {IconLeading && !readonly && (
              <Flex
                className={classNames(
                  sizeStyles[size],
                  redoTextInputCss.leadingIconWrapper,
                )}
              >
                <IconLeading />
              </Flex>
            )}
            <BaseRedoInput
              autoCompleteDisabled={autoCompleteDisabled}
              autoFocus={autoFocus}
              constrainToInputLength={constrainToInputLength}
              datalistOptions={datalistOptions}
              dataTestId={dataTestId}
              htmlPattern={htmlPattern}
              inputTypeHint={inputTypeHint}
              mask={mask}
              max={max}
              maxLength={maxLength}
              min={min}
              name={name}
              onBlur={onBlur}
              onFocus={onFocus}
              onKeyDown={onKeyDown}
              onPaste={onPaste}
              onWheel={onWheel}
              placeholder={placeholder}
              placeholderStyle={placeholderStyle}
              ref={ref}
              required={required}
              setValue={setValue}
              size={size}
              state={state}
              step={step}
              value={value}
            />
            {autoSave && <SaveIndicator isSaving={autoSave.isSaving} />}
            {trailingActions && <>{trailingActions}</>}
            {!trailingActions && IconTrailing && !readonly && (
              <Flex
                className={classNames(
                  sizeStyles[size],
                  redoTextInputCss.trailingIconWrapper,
                )}
              >
                <IconTrailing />
              </Flex>
            )}
            {submit && (
              <RedoButton
                dangerousStyleThatShouldOnlyBeUsedForMerchantBranding={{
                  background: "linear-gradient(90deg,#6bbaf2, #4a90e2 )",
                  border: "none",
                  color: "white",
                }}
                disabled={value.length === 0}
                IconLeading={ArrowUp}
                onClick={submit}
              />
            )}
            {infoTooltip && (
              <BaseRedoInputInfoTooltip
                infoTooltip={infoTooltip}
                size={size}
                state={state}
              />
            )}
          </Flex>
          {toolbar && !readonly && (
            <Flex dir="row" gap="sm">
              {toolbar}
            </Flex>
          )}
        </Flex>
      </BaseRedoInputContainer>
    );
  }),
);
